import React, { useEffect, useRef, useState } from "react";
import Form from "../../components/Form";
import TitleUnderlined from "../../components/TitleUnderlined";
import Button from "../../components/Button";
import Divider from "../../components/Divider";
import APIInterface from "../../tools/APIInterface";
import CloudError from "../../tools/ClourError";
import Attendee from "../../models/Attendee";
import AttendeeStatus from "../../models/AttendeeStatus";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { RoutesWeb } from "../../tools/RoutesWeb";

/** Gap (ms) after which the next keystroke is treated as a new barcode scan. */
const SCAN_BURST_GAP_MS = 300;

const ScanTicketView = () => {
    const token = localStorage.getItem('tokenAdmin');
    const navigate = useNavigate();
    const [code, setCode] = useState('');
    const [consultedCode, setConsultedCode] = useState('');
    const [attendee, setAttendee] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isConsulting, setIsConsulting] = useState(false);
    const lastInputAtRef = useRef(0);
    const folioInputRef = useRef(null);
    /** Sync lock — prevents double Enter before React re-renders. */
    const consultingRef = useRef(false);
    const sealingRef = useRef(false);

    const isBusy = isConsulting || isLoading;

    useEffect(() => {
        if (!token) {
            navigate(RoutesWeb.ADMIN_LOGIN);
        }
    }, [token, navigate]);

    // Always keep focus on the scanner input (never use disabled — it steals focus).
    useEffect(() => {
        if (!isBusy) {
            const timer = setTimeout(() => {
                folioInputRef.current?.focus();
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [isBusy, attendee]);

    const focusFolioInput = () => {
        const input = folioInputRef.current;
        if (!input) {
            return;
        }
        input.focus();
    };

    const handleAuthFailure = (error) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
            localStorage.removeItem('tokenAdmin');
            localStorage.removeItem('userAdmin');
            navigate(RoutesWeb.ADMIN_LOGIN);
        }
    };

    const resetScanner = () => {
        setAttendee(null);
        setCode('');
        setConsultedCode('');
        lastInputAtRef.current = 0;
        setTimeout(() => focusFolioInput(), 0);
    };

    const handleData = (e) => {
        if (consultingRef.current || sealingRef.current) {
            return;
        }

        const now = Date.now();
        const raw = e.target.value;
        const previousCode = code;
        const gap = now - lastInputAtRef.current;
        lastInputAtRef.current = now;

        // New scan after a pause: keep only the new burst (do not concatenate).
        if (gap > SCAN_BURST_GAP_MS && previousCode.length > 0) {
            const next = raw.startsWith(previousCode)
                ? raw.slice(previousCode.length)
                : raw;
            setCode(next.trim());
            if (attendee !== null) {
                setAttendee(null);
                setConsultedCode('');
            }
            return;
        }

        setCode(raw.trim());
    };

    const handleFolioKeyDown = (e) => {
        if (e.key === 'Enter' && (consultingRef.current || sealingRef.current)) {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    const consultTicket = () => {
        if (consultingRef.current || sealingRef.current || !code) {
            return;
        }

        consultingRef.current = true;
        setIsConsulting(true);

        const scannedCode = code;
        setAttendee(null);
        setConsultedCode('');

        APIInterface.validateTicket(token, scannedCode)
            .then((response) => {
                if (response.status === 200) {
                    setAttendee(Attendee.fromJSON(response.data));
                    setConsultedCode(scannedCode);
                    setCode('');
                    lastInputAtRef.current = 0;
                }
            })
            .catch((error) => {
                CloudError.error(error);
                resetScanner();
                handleAuthFailure(error);
            })
            .finally(() => {
                consultingRef.current = false;
                setIsConsulting(false);
                setTimeout(() => focusFolioInput(), 0);
            });
    };

    const updateTicket = (status) => {
        const codeToSeal = consultedCode || code;
        if (sealingRef.current || consultingRef.current || !codeToSeal) {
            return;
        }

        sealingRef.current = true;
        setIsLoading(true);

        APIInterface.updateTicket(token, codeToSeal, { status })
            .then((response) => {
                if (response.status === 200) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Confirmación',
                        text: 'Boleto sellado',
                        showConfirmButton: true,
                    }).then(() => {
                        resetScanner();
                    });
                }
            })
            .catch((error) => {
                CloudError.error(error);
                if (error.response?.status === 409) {
                    setAttendee((prev) => (prev ? { ...prev, status: AttendeeStatus.SCANNED } : prev));
                }
                handleAuthFailure(error);
            })
            .finally(() => {
                sealingRef.current = false;
                setIsLoading(false);
                setTimeout(() => focusFolioInput(), 0);
            });
    };

    const eventTitle = attendee?.order?.event?.title ?? '';
    const eventStartDate = attendee?.order?.event?.startDate ?? '';
    const eventStartTime = attendee?.order?.event?.startTime ?? '';

    return (
        <div className="pt-5 pb-5" id="ticketDetail">
            <TitleUnderlined>Ingreso al <span className="embercom_overlined">Evento</span></TitleUnderlined>
            <br />
            <br />
            <Form submitCallback={consultTicket}>
                <div className="form-group" style={{ maxWidth: '70%' }}>
                    <label htmlFor="folio" className="semibold"><b>Escanee el código de barras</b></label>
                    <div className="d-flex justify-content-between">
                        <input
                            ref={folioInputRef}
                            type="text"
                            className="form-control"
                            id="folio"
                            autoFocus
                            name="barcode-scanner"
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            aria-describedby="folio"
                            required
                            value={code}
                            readOnly={isBusy}
                            onChange={handleData}
                            onKeyDown={handleFolioKeyDown}
                        />
                        <Button
                            className="ml-4"
                            style={{ minWidth: '27%', marginLeft: '2rem' }}
                            disabled={isBusy || !code}
                        >
                            {isConsulting ? 'Consultando...' : 'Consultar'}
                            {!isConsulting && (
                                <img src={require('../../assets/arrow_next.png')} alt="arrowNext" />
                            )}
                        </Button>
                    </div>
                </div>
            </Form>
            <br />
            <br />
            {attendee !== null ? (
                <>
                    <h4 className="text-uppercase"><b>Información del visitante</b></h4>
                    <Divider />
                    <table className="table border-0">
                        <tbody>
                            <tr className="border-0">
                                <td className="border-0">
                                    <label><b>Boleto</b></label><br />
                                    <b>1</b>
                                </td>
                                <td className="border-0">
                                    <div className="form-group">
                                        <label><b>Nombre</b></label>
                                        <input className="form-control" type="text" value={attendee.fullName || ''} disabled />
                                    </div>
                                </td>
                                <td className="border-0" colSpan="2">
                                    <div className="form-group">
                                        <label>Evento</label>
                                        <input className="form-control" type="text" value={eventTitle} disabled />
                                    </div>
                                </td>
                            </tr>
                            <tr className="border-0">
                                <td className="border-0"><b></b></td>
                                <td className="border-0">
                                    <div className="form-group">
                                        <label>Fecha</label>
                                        <input className="form-control" type="text" value={eventStartDate} disabled />
                                    </div>
                                </td>
                                <td className="border-0">
                                    <div className="form-group">
                                        <label>Hora</label>
                                        <input className="form-control" type="text" value={eventStartTime} disabled />
                                    </div>
                                </td>
                                {attendee.email ? (
                                    <td className="border-0">
                                        <div className="form-group">
                                            <label>Correo</label>
                                            <input className="form-control" type="text" value={attendee.email} disabled />
                                        </div>
                                    </td>
                                ) : attendee.phoneNumber ? (
                                    <td className="border-0">
                                        <div className="form-group">
                                            <label>Teléfono</label>
                                            <input className="form-control" type="text" value={attendee.phoneNumber} disabled />
                                        </div>
                                    </td>
                                ) : null}
                            </tr>
                        </tbody>
                    </table>
                    {attendee.status === AttendeeStatus.AVAILABLE ? (
                        <>
                            <div className="available d-flex justify-content-between">
                                <p className="m-0">Boleto disponible</p>
                                <img src={require('../../assets/correct.png')} alt="correct" width="32px" height="29px" />
                            </div>
                            <br />
                            <div className="d-flex justify-content-between">
                                <button
                                    type="button"
                                    className="row m-0 accept align-items-center justify-content-between"
                                    disabled={isBusy}
                                    onClick={() => updateTicket(AttendeeStatus.SCANNED)}
                                >
                                    <div className="col-10">
                                        Sellar ingreso
                                    </div>
                                    <div className="col-2">
                                        {isLoading ? (
                                            <div
                                                className="spinner-border"
                                                role="status"
                                                style={{
                                                    height: '15px',
                                                    width: '15px',
                                                    fontSize: '10px',
                                                    marginLeft: '10px',
                                                }}
                                            />
                                        ) : (
                                            <img src={require('../../assets/arrownext.png')} alt="next" width="20px" />
                                        )}
                                    </div>
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="unavailable d-flex justify-content-between">
                            <p className="m-0">Boleto escaneado</p>
                            x
                        </div>
                    )}
                </>
            ) : null}
        </div>
    );
};

export default ScanTicketView;
